import Pagination from "../../components/ui/Pagination";
import StatusAction from "../../components/ui/StatusAction";
import EnumBadge from "../../components/ui/EnumBadge";
import { enumTone } from "../../components/ui/EnumBadge";
import CompactReference from "../../components/ui/CompactReference";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./TrackingMgtPage.css";
import {
  createTracking,
  deleteTrackingById,
  getTrackings,
  updateTrackingStatus,
} from "../../services/trackingService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TrackingItem, getTrackingUserName } from "../../types/tracking";
import {
  TRACKING_STATUS_OPTIONS,
  formatTrackingStatus,
} from "../../constants/trackingStatuses";
import CreateTrackingModal from "../../compoonents/CreateTrackingModal/CreateTrackingModal";
import DeleteConfirmationModal from "../../compoonents/DeleteConfirmationModal/DeleteConfirmationModal";
import PageLoader from "../../compoonents/PageLoader";
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from "../../compoonents/PageToolbar";
import { FiEye, FiTrash2 } from "react-icons/fi";

import emptyStateIcon from "../../assets/images/trackingicon.png";




const PAGE_SIZE = 10;

const TrackingMgtPage: React.FC = () => {
  const navigate = useNavigate();

  const [trackings, setTrackings] = useState<TrackingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TrackingItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  useEffect(() => {
    const fetchTrackings = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTrackings(searchTerm, filterStatus);
        setTrackings(data);
      } catch {
        setError("Failed to load tracking data.");
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchTrackings, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, filterStatus]);

  const visibleTrackings = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return trackings.filter((t) => {
      const userName = getTrackingUserName(t.user).toLowerCase();
      const email = (t.user?.email ?? "").toLowerCase();
      const matchesSearch =
        !searchTerm ||
        (t.trackingId || "").toLowerCase().includes(lowerSearch) ||
        (t.shipmentId || "").toLowerCase().includes(lowerSearch) ||
        t.id.toLowerCase().includes(lowerSearch) ||
        userName.includes(lowerSearch) ||
        email.includes(lowerSearch);

      const shipmentStatus = (t.shipment?.shipmentStatus ?? "").toLowerCase();
      const matchesStatus = filterStatus
        ? shipmentStatus === filterStatus.toLowerCase()
        : true;

      return matchesSearch && matchesStatus;
    });
  }, [trackings, searchTerm, filterStatus]);

  const totalPages = Math.max(
    1,
    Math.ceil(visibleTrackings.length / PAGE_SIZE),
  );
  const paginatedTrackings = visibleTrackings.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleStatusChange = async (entryId: string, newStatus: string) => {
    try {
      await updateTrackingStatus(entryId, newStatus);
      setTrackings((current) =>
        current.map((t) =>
          t.id === entryId && t.shipment
            ? { ...t, shipment: { ...t.shipment, shipmentStatus: newStatus } }
            : t,
        ),
      );
      toast.success("Tracking status updated");
    } catch {
      toast.error("Failed to update tracking status");
    }
  };

  const handleCreate = async (shipmentId: string) => {
    setIsCreating(true);
    try {
      await createTracking(shipmentId);
      toast.success("Tracking created");
      setShowCreateModal(false);
      const data = await getTrackings(searchTerm, filterStatus);
      setTrackings(data);
    } catch {
      toast.error("Failed to create tracking. It may already exist.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteTrackingById(deleteTarget.id);
      setTrackings((curr) => curr.filter((t) => t.id !== deleteTarget.id));
      toast.success("Tracking deleted");
      setDeleteTarget(null);
    } catch {
      toast.error("Failed to delete tracking");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>

      <div className="tracking-mgt-page">
        <header className="page-header">
          <h3>Tracking Management</h3>
          <div className="page-controls">
            <button
              type="button"
              className="create-tracking-btn"
              onClick={() => setShowCreateModal(true)}
            >
              + Create Tracking
            </button>

            <PageToolbar>
              <PageFilterDropdown
                value={filterStatus}
                options={TRACKING_STATUS_OPTIONS}
                onChange={setFilterStatus}
                allLabel="All Statuses"
              />
              <PageSearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by ID, name, email..."
              />
            </PageToolbar>
          </div>
        </header>

        {error && <div className="page-error">{error}</div>}

        {loading && !error && <PageLoader message="Loading tracking data..." />}

        {!loading && !error && (
          <>
            {visibleTrackings.length === 0 ? (
              <div className="tracking-mgt-page page--empty">
                {searchTerm || filterStatus ? (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <h3>No results found</h3>
                  </div>
                ) : (
                  <>
                    <img src={emptyStateIcon} alt="No shipments" />
                    <h3>No tracking entries found</h3>
                  </>
                )}
              </div>
            ) : (
              <div className="table-container admin-table-container">
                <table className="data-table admin-data-table">
                  <thead>
                    <tr>
                      <th>Shipment ID</th>
                      <th>Tracking ID</th>
                      <th>Customer Name</th>
                      <th>Email</th>
                      <th>Delivery Type</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTrackings.map((tracking) => {
                      const status =
                        tracking.shipment?.shipmentStatus ?? "pending";
                      return (
                        <tr key={tracking.id}>
                          <td data-label="Shipment ID">
                            <CompactReference value={tracking.shipmentId} />
                          </td>
                          <td data-label="Tracking ID">
                            <CompactReference value={tracking.trackingId} />
                          </td>
                          <td data-label="Customer Name">
                            {getTrackingUserName(tracking.user)}
                          </td>
                          <td data-label="Email">
                            {tracking.user?.email || "—"}
                          </td>
                          <td data-label="Delivery Type">
                            <EnumBadge value={tracking.shipment?.deliveryTypeTo} />
                          </td>
                          <td className="status-cell" data-label="Status">
                            <StatusAction value={status} label={formatTrackingStatus(status)} badgeClassName={`navo-enum-badge navo-enum-badge--${enumTone(status)}`} options={TRACKING_STATUS_OPTIONS} onChange={value => handleStatusChange(tracking.id, value)} />
                          </td>
                          <td data-label="Actions">
                            <div className="table-action-buttons">
                              <button
                                type="button"
                                className="table-action-btn"
                                title="View tracking"
                                aria-label="View tracking"
                                onClick={() =>
                                  navigate(`/tracking/${tracking.id}`)
                                }
                              >
                                <FiEye />
                              </button>
                              <button
                                type="button"
                                className="table-action-btn danger"
                                title="Delete tracking"
                                aria-label="Delete tracking"
                                onClick={() => setDeleteTarget(tracking)}
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && visibleTrackings.length > 0 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} loading={loading} />
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <CreateTrackingModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
          isSubmitting={isCreating}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmationModal
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
          itemName={deleteTarget.trackingId || deleteTarget.id}
        />
      )}
    </>
  );
};

export default TrackingMgtPage;
